'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { JUPITER_LAYER } from 'HubStyleTokens/sizes';
import PropTypes from 'prop-types';
import { Fragment, forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { createPortal, findDOMNode } from 'react-dom';
import useUniqueId from 'react-utils/hooks/useUniqueId';
import invariant from 'react-utils/invariant';
import useFactory from '../hooks/useFactory';
import { addToListAttr, getIframeAwareAncestorElements, getIframeAwareSurroundingWindows, getRelativeRect, removeFromListAttr } from '../utils/Dom';
import domElementPropType from '../utils/propTypes/domElement';
import { getCollisionRect } from '../utils/Rects';
import { getDefaultNode } from '../utils/RootNode';
import { debounceByTick } from '../utils/Timers';
import { makeFloatyLayerElement } from './utils/Layer';
import { getElementDimensions, getTransformForPosition, getViewportBounds, isCollisionFree } from './utils/Placement';
import ChildrenWrapper from '../utils/ChildrenWrapper';
const UIAbstractFloaty = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    bodyElement,
    children,
    computePosition,
    content,
    _hideContentWhenMissingTarget,
    layer,
    layerClassName,
    onPositionChange,
    target,
    zIndex
  } = props;
  /** Internally-created layer id */

  const uniqueId = useUniqueId(`uiabstractfloaty-`);
  /** The DOM element where the layer element will be mounted */

  const rootNode = bodyElement || getDefaultNode();
  /** The DOM element where the content will be rendered */

  const layerElement = useFactory(() => {
    return layer || makeFloatyLayerElement(rootNode, uniqueId, zIndex, layerClassName);
  });

  if (process.env.NODE_ENV !== 'production') {
    invariant(!(layerElement.id == null || layerElement.id === '' || layerElement.id === 'null' || layerElement.id === 'undefined'), 'Layer element must have an `id` attribute');
  }
  /** A ref to the wrapper around `children` */


  const [childrenWrapperRef, setChildrenWrapperRef] = useState();
  /** The DOM element the floaty is attached to (`target` if given, `children` otherwise) */

  const targetElement = target || findDOMNode(childrenWrapperRef); // Persist the latest targetElement in a ref

  const targetElementRef = useRef();
  targetElementRef.current = targetElement; // Persist the latest `computePosition` callback in a ref

  const computePositionRef = useRef();
  computePositionRef.current = computePosition; // Persist the latest `onPositionChange` callback in a ref

  const onPositionChangeRef = useRef();
  onPositionChangeRef.current = onPositionChange;
  /** Call `computePosition` and move the `layerElement` */

  const reposition = () => {
    if (!targetElementRef.current) return;
    const targetBounds = getRelativeRect(targetElementRef.current, layerElement.offsetParent);
    const contentDimensions = getElementDimensions(layerElement);
    const position = computePositionRef.current({
      targetBounds,
      contentDimensions
    });
    const positionTransform = getTransformForPosition(position);
    /* Check to make sure the popover is still in the viewport */

    const viewportCollisionRect = getCollisionRect(position, getViewportBounds());

    if (layerElement.style.transform !== positionTransform) {
      layerElement.style.transform = positionTransform;
      if (onPositionChangeRef.current) onPositionChangeRef.current();
    } else if (!isCollisionFree(viewportCollisionRect)) {
      /* Trigger onPositionChange if the transform doesn't change but is now out of bounds  (#8706) */
      if (onPositionChangeRef.current) onPositionChangeRef.current();
    }
  }; // Expose `reposition()` as a public method


  useImperativeHandle(ref, () => ({
    reposition
  }));
  useEffect(() => {
    layerElement.style.zIndex = zIndex;
  }, [layerElement, zIndex]); // Recompute the layer element's position immediately after every render

  useLayoutEffect(reposition); // Bind event handlers that trigger reposition

  useEffect(() => {
    if (!targetElement) return undefined; // Debounce repositioning triggered by events to prevent scroll jank

    const debouncedReposition = debounceByTick(reposition); // Reposition whenever DOM mutations occur within the content layer

    const mutationObserver = new MutationObserver(mutationRecords => {
      // Ignore observed mutations that were caused by `reposition()` itself.
      if (mutationRecords.every(mutationRecord => mutationRecord.target === layerElement)) {
        return;
      }

      debouncedReposition();
    });
    mutationObserver.observe(layerElement, {
      attributes: true,
      childList: true,
      subtree: true
    }); // Reposition whenever image `load` events occur in the target element

    targetElement.addEventListener('load', debouncedReposition, true); // Reposition on window-level scroll/resize

    const surroundingWindows = getIframeAwareSurroundingWindows(targetElement);
    surroundingWindows.forEach(surroundingWindow => {
      surroundingWindow.addEventListener('resize', debouncedReposition);
      surroundingWindow.addEventListener('scroll', debouncedReposition);
    }); // Reposition on scroll in any ancestor of the target

    const ancestorElements = getIframeAwareAncestorElements(targetElement);
    ancestorElements.forEach(ancestorElement => {
      ancestorElement.addEventListener('scroll', debouncedReposition);
    }); // Set `data-popover-id` on the target

    addToListAttr(targetElement, 'data-popover-id', layerElement.id);
    return () => {
      mutationObserver.disconnect();
      targetElement.removeEventListener('load', debouncedReposition, true);
      surroundingWindows.forEach(surroundingWindow => {
        try {
          surroundingWindow.removeEventListener('resize', debouncedReposition);
          surroundingWindow.removeEventListener('scroll', debouncedReposition);
        } catch (err) {
          return;
        }
      });
      ancestorElements.forEach(ancestorElement => {
        ancestorElement.removeEventListener('scroll', debouncedReposition);
      });
      removeFromListAttr(targetElement, 'data-popover-id', layerElement.id);
    };
  }, [targetElement]); // eslint-disable-line react-hooks/exhaustive-deps
  // Remove the layer element on unmount

  useEffect(() => {
    return () => {
      // Only remove the layerElement when it is internally-created
      if (layerElement && layerElement.parentElement && layerElement.id === uniqueId) {
        layerElement.parentElement.removeChild(layerElement);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return /*#__PURE__*/_jsxs(Fragment, {
    children: [children && /*#__PURE__*/_jsx(ChildrenWrapper, {
      ref: setChildrenWrapperRef,
      children: children
    }), _hideContentWhenMissingTarget ? targetElement && content && /*#__PURE__*/createPortal(content, layerElement) : content && /*#__PURE__*/createPortal(content, layerElement)]
  });
});
UIAbstractFloaty.displayName = 'UIAbstractFloaty';
UIAbstractFloaty.propTypes = {
  bodyElement: domElementPropType,
  children: PropTypes.node,
  computePosition: PropTypes.func.isRequired,
  content: PropTypes.node,
  _hideContentWhenMissingTarget: PropTypes.bool.isRequired,
  layer: domElementPropType,

  /** className attached to the layer (root element) that renders the `content`. Ignored when using the `layer` prop. */
  layerClassName: PropTypes.string,

  /** An event handler that gets invoked when the layer is repositioned */
  onPositionChange: PropTypes.func,
  target: domElementPropType,
  zIndex: PropTypes.number.isRequired
};
UIAbstractFloaty.defaultProps = {
  _hideContentWhenMissingTarget: true,
  zIndex: parseInt(JUPITER_LAYER, 10)
};
export default UIAbstractFloaty;